/**
 * Admin: Extract Events from Web Sources with AI
 *
 * Job 2 of the pipeline. Reads the websites we already know about and pulls the
 * actual events out of them. Wraps the `startQueueBasedProcessing` Function
 * endpoint, which first probes Website sources through the extraction gate and
 * sends only candidates to `partner-processing` when enforcement is enabled.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BarChart3, Play, RefreshCw, Server, Square } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  getQueueStats,
  getEventExtractionStatus,
  getExtractionGateImpact,
  startQueueBasedProcessing,
  saveLastRun,
  loadLastRun,
  type QueueStatsResponse,
  type EventExtractionParams,
  type EventExtractionResponse,
  type ExtractionGateImpactResponse,
  type LastRunRecord,
} from '../services/adminApi';
import {
  Card,
  CardTitle,
  CardDescription,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  RunningIndicator,
  ConfirmDialog,
  Banner,
  StatGrid,
  Metric,
  RawJson,
  LastRunCard,
  Field,
  Label,
  Input,
  Select,
  InlineLabel,
  ButtonRow,
  SampleTable,
} from '../components/admin/AdminUI';
import { displayEntityType } from '../utils/entityTerminology';

const ACTION = 'startQueueBasedProcessing';

type Scope = 'all' | 'entityType' | 'entity' | 'eventSource' | 'eventSourceType';
type Mode = 'allBatches' | 'singleBatch';
const ENTITY_TYPES = ['Partner', 'Company', 'Organization', 'Industry', 'City', 'University'];

function rate(value: number | null): string {
  return value == null ? 'Measuring…' : `${value.toFixed(1)}%`;
}

export const AdminReprocessPage: React.FC = () => {
  const { userInfo } = useAuth();

  // Form state
  const [scope, setScope] = useState<Scope>('all');
  const [entityType, setEntityType] = useState<string>('University');
  const [entityId, setEntityId] = useState<string>('');
  const [eventSourceId, setEventSourceId] = useState<string>('');
  const [eventSourceTypeId, setEventSourceTypeId] = useState<string>('');
  const [batchSize, setBatchSize] = useState<number>(500);
  const [mode, setMode] = useState<Mode>('allBatches');
  const [offset, setOffset] = useState<number>(0);
  const [forceExtraction, setForceExtraction] = useState(false);

  // Run state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [result, setResult] = useState<EventExtractionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<LastRunRecord<EventExtractionResponse> | null>(
    null,
  );

  // Live progress (client-side loop in allBatches mode)
  interface LoopProgress {
    batches: number;
    enqueued: number;
    errors: number;
    currentOffset: number;
    nextOffset: number | null;
    hasMore: boolean;
    lastBatchMs: number | null;
    aborted: boolean;
  }
  const [progress, setProgress] = useState<LoopProgress | null>(null);
  const abortRef = useRef<boolean>(false);

  // Queue stats
  const [queue, setQueue] = useState<QueueStatsResponse | null>(null);
  const [queueErr, setQueueErr] = useState<string | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueUnavailable, setQueueUnavailable] = useState(false);
  const [queueUpdatedAt, setQueueUpdatedAt] = useState<Date | null>(null);

  // Impact reporting is independent of the currently edited run form so
  // changing the processing scope cannot silently change dashboard totals.
  const [impact, setImpact] = useState<ExtractionGateImpactResponse | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactError, setImpactError] = useState<string | null>(null);
  const [impactDays, setImpactDays] = useState(90);

  const refreshQueue = useCallback(async () => {
    setQueueLoading(true);
    setQueueErr(null);
    try {
      const r = await getQueueStats();
      if (r === null) {
        setQueueUnavailable(true);
        setQueue(null);
      } else {
        setQueueUnavailable(false);
        setQueue(r);
      }
      setQueueUpdatedAt(new Date());
    } catch (e: any) {
      setQueueErr(e?.message ?? 'Failed to load queue stats');
    } finally {
      setQueueLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshQueue();
  }, [refreshQueue]);

  const refreshImpact = useCallback(async () => {
    setImpactLoading(true);
    setImpactError(null);
    try {
      setImpact(await getExtractionGateImpact({ days: impactDays }));
    } catch (e: any) {
      setImpactError(e?.message ?? 'Failed to load extraction gate impact');
    } finally {
      setImpactLoading(false);
    }
  }, [impactDays]);

  useEffect(() => {
    refreshImpact();
  }, [refreshImpact]);

  // Auto-refresh queue while a run is in progress.
  useEffect(() => {
    if (!running || queueUnavailable) return;
    const id = window.setInterval(() => {
      refreshQueue();
    }, 5000);
    return () => window.clearInterval(id);
  }, [running, queueUnavailable, refreshQueue]);

  useEffect(() => {
    setLastRun(loadLastRun<EventExtractionResponse>(ACTION));
  }, []);

  // Validation
  const scopeValid = useMemo(() => {
    switch (scope) {
      case 'entityType':      return entityType !== '';
      case 'entity':          return entityId.trim() !== '' && Number(entityId) > 0;
      case 'eventSource':     return eventSourceId.trim() !== '' && Number(eventSourceId) > 0;
      case 'eventSourceType': return eventSourceTypeId.trim() !== '';
      default:                return true;
    }
  }, [scope, entityType, entityId, eventSourceId, eventSourceTypeId]);

  const batchValid = batchSize >= 1;

  const buildParams = (overrides?: {
    offset?: number;
    processAllBatches?: boolean;
  }): EventExtractionParams => {
    const p: EventExtractionParams = {
      batchSize,
      processAllBatches: overrides?.processAllBatches ?? (mode === 'allBatches'),
      forceExtraction,
    };
    if (overrides?.offset != null) p.offset = overrides.offset;
    else if (mode === 'singleBatch') p.offset = offset;
    if (scope === 'entityType')      p.entityTypes = [entityType];
    if (scope === 'entity')          p.entityId = Number(entityId);
    if (scope === 'eventSource')     p.eventSourceId = Number(eventSourceId);
    if (scope === 'eventSourceType') p.eventSourceTypeId = Number(eventSourceTypeId);
    return p;
  };

  const waitForGateProcess = async (
    initial: EventExtractionResponse,
  ): Promise<EventExtractionResponse> => {
    // Gate-enabled starts return 202 before source probing/heavy extraction is
    // complete. Legacy responses have no gate marker and remain synchronous.
    if (!initial.gateEnabled || !initial.processId) return initial;
    let latest = initial;
    const terminal = new Set(['completed', 'completed_with_errors', 'failed']);
    while (!terminal.has(String(latest.status || '').toLowerCase()) && !abortRef.current) {
      setResult(latest);
      const runs = latest.runs;
      // `enqueued` represents sources accepted for heavy AI, not all sources
      // probed; skipped unchanged sources are reported separately in `runs`.
      setProgress({
        batches: 1,
        enqueued: (runs?.dispatching ?? 0) + (runs?.dispatched ?? 0) +
          (runs?.completed ?? 0) + (runs?.failed ?? 0),
        errors: runs?.failed ?? 0,
        currentOffset: 0,
        nextOffset: null,
        hasMore: true,
        lastBatchMs: latest.processingTime,
        aborted: false,
      });
      await new Promise((resolve) => setTimeout(resolve, 3000));
      if (!abortRef.current) latest = await getEventExtractionStatus(initial.processId);
    }
    setProgress((current) => current ? {
      ...current,
      hasMore: false,
      aborted: abortRef.current,
      lastBatchMs: latest.processingTime,
    } : current);
    return latest;
  };

  const handleStart = () => {
    if (!scopeValid || !batchValid || running) return;
    setConfirmOpen(true);
  };

  const handleStop = () => {
    abortRef.current = true;
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setRunning(true);
    setError(null);
    setResult(null);
    setProgress(null);
    abortRef.current = false;
    const overallStart = Date.now();
    setStartedAt(overallStart);

    try {
      // ----- Single-batch mode: one call, no client-side loop -----
      if (mode === 'singleBatch') {
        const started = await startQueueBasedProcessing(buildParams());
        const data = await waitForGateProcess(started);
        setResult(data);
        const record: LastRunRecord<EventExtractionResponse> = {
          timestamp: new Date().toISOString(),
          triggeredBy: userInfo.userName || userInfo.userId,
          durationMs: data.processingTime,
          summary: {
            enqueued: data.enqueuedCount,
            errors: data.errorCount,
            batches: data.batchesProcessed ?? 1,
          },
          response: data,
        };
        saveLastRun(ACTION, record);
        setLastRun(record);
        await refreshImpact();
        return;
      }

      // ----- All-batches mode: client-side loop, one batch per call -----
      // The server's processAllBatches=true is bypassed here because that
      // server-side loop can hit the function timeout on large data sets and
      // silently stop. Looping here gives accurate progress + the ability to
      // stop, and matches the proven `processAllBatches` script.
      let currentOffset = 0;
      let totalEnqueued = 0;
      let totalErrors = 0;
      let batches = 0;
      let lastResponse: EventExtractionResponse | null = null;
      let aggregatedErrors: EventExtractionResponse['errors'] = [];
      let hasMore = true;

      setProgress({
        batches: 0,
        enqueued: 0,
        errors: 0,
        currentOffset: 0,
        nextOffset: null,
        hasMore: true,
        lastBatchMs: null,
        aborted: false,
      });

      while (hasMore) {
        if (abortRef.current) break;

        const data = await startQueueBasedProcessing(
          buildParams({ offset: currentOffset, processAllBatches: false }),
        );
        if (data.gateEnabled) {
          // The server performs keyset paging for a gate batch, so the old
          // browser-side OFFSET loop must stop after this single async process.
          const settled = await waitForGateProcess(data);
          setResult(settled);
          const record: LastRunRecord<EventExtractionResponse> = {
            timestamp: new Date().toISOString(),
            triggeredBy: userInfo.userName || userInfo.userId,
            durationMs: settled.processingTime,
            summary: {
              probed: settled.runs?.total ?? 0,
              // Completed heavy runs are the most useful final candidate count;
              // fall back to the compatibility counter for partial responses.
              candidates: settled.runs?.completed ?? settled.enqueuedCount,
              skipped: settled.runs?.skipped ?? 0,
              errors: settled.runs?.failed ?? settled.errorCount,
            },
            response: settled,
          };
          saveLastRun(ACTION, record);
          setLastRun(record);
          await refreshImpact();
          return;
        }
        lastResponse = data;

        if (!data.success) {
          throw new Error(data.error ?? data.message ?? 'Batch failed');
        }

        batches += 1;
        totalEnqueued += data.enqueuedCount ?? 0;
        totalErrors += data.errorCount ?? 0;
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          // Keep at most ~50 sample errors to avoid bloating state.
          aggregatedErrors = aggregatedErrors
            .concat(data.errors)
            .slice(0, 50);
        }
        hasMore = !!data.hasMoreBatches;
        const next =
          data.nextOffset != null ? data.nextOffset : currentOffset + batchSize;

        setProgress({
          batches,
          enqueued: totalEnqueued,
          errors: totalErrors,
          currentOffset,
          nextOffset: hasMore ? next : null,
          hasMore,
          lastBatchMs: data.processingTime,
          aborted: false,
        });

        currentOffset = next;

        // Mirror the script's small inter-batch delay; also yields the event
        // loop so React can paint progress.
        if (hasMore && !abortRef.current) {
          await new Promise((r) => setTimeout(r, 500));
        }
      }

      const aborted = abortRef.current;
      const totalMs = Date.now() - overallStart;

      // Synthesize a final response from the loop totals.
      const synthetic: EventExtractionResponse = {
        success: true,
        message: aborted
          ? `Stopped by user after ${batches} batch(es).`
          : `Looped through ${batches} batch(es).`,
        enqueuedCount: totalEnqueued,
        errorCount: totalErrors,
        batchSize,
        batchesProcessed: batches,
        totalRecords: lastResponse?.totalRecords,
        offset: 0,
        hasMoreBatches: aborted ? hasMore : false,
        nextOffset: aborted && hasMore ? currentOffset : undefined,
        processingTime: totalMs,
        errors: aggregatedErrors,
      };
      setResult(synthetic);
      setProgress((p) => (p ? { ...p, aborted } : p));

      const record: LastRunRecord<EventExtractionResponse> = {
        timestamp: new Date().toISOString(),
        triggeredBy: userInfo.userName || userInfo.userId,
        durationMs: totalMs,
        summary: {
          enqueued: totalEnqueued,
          errors: totalErrors,
          batches,
        },
        response: synthetic,
      };
      saveLastRun(ACTION, record);
      setLastRun(record);
    } catch (e: any) {
      setError(e?.message ?? 'Unknown error');
    } finally {
      setRunning(false);
      setStartedAt(null);
      abortRef.current = false;
    }
  };

  // Helpful "next page" handler in single-batch mode
  const handleAdvanceOffset = () => {
    if (result?.nextOffset != null) setOffset(result.nextOffset);
  };

  return (
    <AdminLayout
      currentPage="extractEvents"
      pageTitle="Extract Events from Web Sources with AI"
      pageDescription="Job 2 — read the websites we already know about and pull the actual events out of them."
    >
      <Card>
        <CardTitle>
          <Server size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Processing queues
        </CardTitle>
        <CardDescription>
          Live depth of the queues that drive event extraction. Auto-refreshes
          every 5 seconds while a run is in progress.
        </CardDescription>

        {queueErr && <Banner tone="error">{queueErr}</Banner>}

        {queueUnavailable && !queueErr && (
          <Banner tone="info">
            <strong>Queue stats endpoint not available.</strong> The backend
            does not expose <code>/api/getQueueStats</code> yet.
          </Banner>
        )}

        {queue && (
          <>
            <StatGrid>
              <Metric
                label="Total queue depth"
                value={(queue.queues?.reduce(
                  (s, q) => s + (q.approximateMessageCount ?? 0),
                  0,
                ) ?? 0).toLocaleString()}
              />
              <Metric
                label="Poison messages"
                value={(queue.queues?.reduce(
                  (s, q) => s + (q.poisonMessageCount ?? 0),
                  0,
                ) ?? 0).toLocaleString()}
              />
              {queue.functionApp?.state && (
                <Metric
                  label="Function App"
                  value={queue.functionApp.state}
                />
              )}
              {typeof queue.functionApp?.instanceCount === 'number' && (
                <Metric label="Instances" value={queue.functionApp.instanceCount} />
              )}
            </StatGrid>

            {queue.queues && queue.queues.length > 0 && (
              <SampleTable>
                <thead>
                  <tr>
                    <th>Queue</th>
                    <th style={{ textAlign: 'right' }}>Depth</th>
                    <th style={{ textAlign: 'right' }}>Poison</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.queues.map((q) => (
                    <tr key={q.name}>
                      <td><code>{q.name}</code></td>
                      <td style={{ textAlign: 'right' }}>
                        {q.approximateMessageCount.toLocaleString()}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {(q.poisonMessageCount ?? 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </SampleTable>
            )}
          </>
        )}

        <ButtonRow>
          <SecondaryButton onClick={refreshQueue} disabled={queueLoading}>
            <RefreshCw size={16} />
            {queueLoading ? 'Refreshing…' : 'Refresh'}
          </SecondaryButton>
          {queueUpdatedAt && (
            <span style={{ fontSize: 12, color: '#6b7280', alignSelf: 'center' }}>
              Updated {queueUpdatedAt.toLocaleTimeString()}
            </span>
          )}
        </ButtonRow>
      </Card>

      <Card>
        <CardTitle>
          <BarChart3 size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Extraction gate impact
        </CardTitle>
        <CardDescription>
          Measured AI work avoided by unchanged-content gates, forward event
          coverage, and Jina text extraction. Screenshot comparison uses the
          same source cohort before and after gate rollout.
        </CardDescription>

        <ButtonRow>
          <Field style={{ margin: 0, minWidth: 150 }}>
            <Label htmlFor="impactDays">Measurement window</Label>
            <Select
              id="impactDays"
              value={impactDays}
              onChange={(event) => setImpactDays(Number(event.target.value))}
            >
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
            </Select>
          </Field>
          <SecondaryButton onClick={refreshImpact} disabled={impactLoading}>
            <RefreshCw size={16} />
            {impactLoading ? 'Measuring…' : 'Refresh impact'}
          </SecondaryButton>
        </ButtonRow>

        {impactError && <Banner tone="error">{impactError}</Banner>}

        {impact && (
          <>
            {impact.rollout.maturity !== 'repeat_measurements' && (
              <Banner tone="info" style={{ marginTop: 16 }}>
                <strong>Baseline phase:</strong> the first successful shadow pass
                establishes trusted hashes and event coverage. Re-run the same
                sources to measure the percentage that would be skipped as
                unchanged. AI is still executed for every source in shadow mode.
              </Banner>
            )}

            <StatGrid style={{ marginTop: 16 }}>
              <Metric
                label="Potential AI reduction"
                value={rate(impact.savings.potentialAiReductionPct)}
                hint={`${impact.savings.avoidableAiRuns.toLocaleString()} of ${impact.savings.measuredDecisionRuns.toLocaleString()} repeat decisions`}
                tone={impact.savings.potentialAiReductionPct != null ? 'success' : 'neutral'}
              />
              <Metric
                label="Jina usable text"
                value={rate(impact.jina.usableRatePct)}
                hint={`${impact.jina.usableRuns.toLocaleString()} of ${impact.savings.completedProbeRuns.toLocaleString()} completed probes`}
              />
              <Metric
                label="Screenshot rate"
                value={`${rate(impact.screenshots.beforeJina.screenshotRatePct)} → ${rate(impact.screenshots.withJina.screenshotRatePct)}`}
                hint={!impact.screenshots.comparisonReady
                  ? `${impact.savings.pendingHeavyRuns.toLocaleString()} gated heavy runs still pending`
                  : impact.screenshots.relativeReductionPct == null
                    ? 'No comparable before-Jina screenshot rate'
                    : impact.screenshots.relativeReductionPct >= 0
                      ? `${impact.screenshots.relativeReductionPct.toFixed(1)}% relative reduction`
                      : `${Math.abs(impact.screenshots.relativeReductionPct).toFixed(1)}% relative increase`}
                tone={impact.screenshots.comparisonReady &&
                  impact.screenshots.relativeReductionPct != null &&
                  impact.screenshots.relativeReductionPct > 0 ? 'success' :
                  impact.screenshots.comparisonReady &&
                  impact.screenshots.relativeReductionPct != null &&
                  impact.screenshots.relativeReductionPct < 0 ? 'warning' : 'neutral'}
              />
              <Metric
                label="Coverage-qualified"
                value={impact.coverage.qualifiedSources.toLocaleString()}
                hint={`${rate(impact.coverage.qualifiedPct)} of baselined sources`}
              />
              <Metric
                label="Baselined sources"
                value={`${impact.coverage.baselinedSources.toLocaleString()} / ${impact.coverage.activeWebsiteSources.toLocaleString()}`}
                hint={`${rate(impact.coverage.baselineCoveragePct)} rollout coverage`}
              />
              <Metric
                label="Reader fallbacks"
                value={(impact.jina.probeFailedRuns + impact.jina.unusableTextRuns).toLocaleString()}
                hint={`${impact.jina.probeFailedRuns} failed · ${impact.jina.unusableTextRuns} unusable`}
                tone={impact.jina.probeFailedRuns > 0 ? 'warning' : 'neutral'}
              />
            </StatGrid>

            <CardTitle style={{ fontSize: 16, marginTop: 24 }}>
              How long coverage-qualified sources can wait
            </CardTitle>
            <CardDescription>
              Cumulative counts. Sources qualify only when they have at least{' '}
              {impact.coverage.policy.minimumUpcomingEvents} upcoming public events
              and reach {impact.coverage.policy.horizonDays} days forward.
            </CardDescription>
            <SampleTable>
              <thead>
                <tr>
                  <th>Can wait at least</th>
                  <th style={{ textAlign: 'right' }}>Sources</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1 week</td><td style={{ textAlign: 'right' }}>{impact.coverage.canWaitAtLeast.oneWeek.toLocaleString()}</td></tr>
                <tr><td>2 weeks</td><td style={{ textAlign: 'right' }}>{impact.coverage.canWaitAtLeast.twoWeeks.toLocaleString()}</td></tr>
                <tr><td>1 month</td><td style={{ textAlign: 'right' }}>{impact.coverage.canWaitAtLeast.oneMonth.toLocaleString()}</td></tr>
                <tr><td>2 months</td><td style={{ textAlign: 'right' }}>{impact.coverage.canWaitAtLeast.twoMonths.toLocaleString()}</td></tr>
                <tr><td>3+ months</td><td style={{ textAlign: 'right' }}>{impact.coverage.canWaitAtLeast.threeMonths.toLocaleString()}</td></tr>
              </tbody>
            </SampleTable>

            <CardTitle style={{ fontSize: 16, marginTop: 24 }}>
              Screenshot comparison
            </CardTitle>
            <SampleTable>
              <thead>
                <tr>
                  <th>Period</th>
                  <th style={{ textAlign: 'right' }}>Measured runs</th>
                  <th style={{ textAlign: 'right' }}>Screenshots</th>
                  <th style={{ textAlign: 'right' }}>Rate</th>
                  <th style={{ textAlign: 'right' }}>Jina text</th>
                  <th style={{ textAlign: 'right' }}>Local DOM</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Same processed sources before Jina</td>
                  <td style={{ textAlign: 'right' }}>{impact.screenshots.beforeJina.measuredMethodRuns.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{impact.screenshots.beforeJina.screenshotRuns.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{rate(impact.screenshots.beforeJina.screenshotRatePct)}</td>
                  <td style={{ textAlign: 'right' }}>—</td>
                  <td style={{ textAlign: 'right' }}>—</td>
                </tr>
                <tr>
                  <td>Gate + Jina</td>
                  <td style={{ textAlign: 'right' }}>{impact.screenshots.withJina.measuredMethodRuns.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{impact.screenshots.withJina.screenshotRuns.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{rate(impact.screenshots.withJina.screenshotRatePct)}</td>
                  <td style={{ textAlign: 'right' }}>{impact.screenshots.withJina.jinaTextRuns.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{impact.screenshots.withJina.localDomTextRuns.toLocaleString()}</td>
                </tr>
              </tbody>
            </SampleTable>

            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 12 }}>
              Generated {new Date(impact.generatedAt).toLocaleString()} · mode{' '}
              <strong>{impact.rollout.gateMode}</strong> · normalizer{' '}
              <strong>{impact.rollout.normalizationVersion}</strong> ·{' '}
              {impact.savings.uniqueSourcesProbed.toLocaleString()} unique sources probed
            </div>
          </>
        )}
      </Card>

      <Card>
        <CardTitle>Extract events from every known web source</CardTitle>
        <CardDescription>
          The default action. This walks every event source we already know
          about, in pages of <strong>{batchSize}</strong>, probes normalized page
          text for meaningful change, and sends AI candidates to the{' '}
          <code>partner-processing</code> queue. Shadow mode still sends every
          source while measuring future savings. This does <strong>not</strong>{' '}
          search the web for new websites &mdash; use{' '}
          <strong>Refresh Event Sources with WebIQ</strong> for that. You can stop
          the loop at any time without losing the batches already queued.
        </CardDescription>

        <Field>
          <Label htmlFor="primaryBatchSize">Batch size</Label>
          <Input
            id="primaryBatchSize"
            type="number"
            min={1}
            max={1000}
            step={50}
            value={batchSize}
            disabled={running}
            onChange={(e) => setBatchSize(Number(e.target.value) || 1)}
            style={{ maxWidth: 160 }}
          />
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
            Sources enumerated per gate page. Default 500. Probe workers still
            apply their own bounded concurrency.
          </div>
        </Field>

        <ButtonRow>
          <PrimaryButton
            onClick={handleStart}
            disabled={!scopeValid || !batchValid || running}
          >
            <Play size={16} />
            {scope === 'all' && mode === 'allBatches'
              ? 'Re-process all sites'
              : 'Start re-processing'}
          </PrimaryButton>
          {running && mode === 'allBatches' && (
            <DangerButton onClick={handleStop}>
              <Square size={14} />
              Stop after current batch
            </DangerButton>
          )}
        </ButtonRow>

        {running && startedAt != null && (
          <RunningIndicator
            startedAt={startedAt}
            message={
              progress
                ? `Looping batches… batch ${progress.batches}, offset ${progress.currentOffset.toLocaleString()}, enqueued ${progress.enqueued.toLocaleString()}`
                : 'Starting extraction preflight…'
            }
          />
        )}

        {progress && (
          <StatGrid style={{ marginTop: 16 }}>
            <Metric label="Batches done" value={progress.batches} />
            <Metric
              label="Enqueued"
              value={progress.enqueued.toLocaleString()}
              tone="success"
            />
            <Metric
              label="Errors"
              value={progress.errors.toLocaleString()}
              tone={progress.errors > 0 ? 'warning' : 'neutral'}
            />
            <Metric
              label="Current offset"
              value={progress.currentOffset.toLocaleString()}
            />
            {progress.lastBatchMs != null && (
              <Metric
                label="Last batch"
                value={`${(progress.lastBatchMs / 1000).toFixed(1)}s`}
              />
            )}
            <Metric
              label="More remaining?"
              value={progress.hasMore ? 'Yes' : 'No'}
              tone={progress.hasMore ? 'warning' : 'success'}
            />
          </StatGrid>
        )}

        {error && <Banner tone="error" style={{ marginTop: 16 }}>{error}</Banner>}
      </Card>

      <Card>
        <CardTitle>Limit scope (optional)</CardTitle>
        <CardDescription>
          By default all records are re-processed. Choose a narrower scope
          here if you only want to re-run a subset.
        </CardDescription>

        <Field>
          <Label>Scope</Label>
          <div>
            <InlineLabel>
              <input
                type="radio"
                name="scope"
                value="entityType"
                checked={scope === 'entityType'}
                onChange={() => setScope('entityType')}
              />
              One entity type
            </InlineLabel>
            <InlineLabel>
              <input
                type="radio"
                name="scope"
                value="all"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
              />
              All records (default)
            </InlineLabel>
            <InlineLabel>
              <input
                type="radio"
                name="scope"
                value="entity"
                checked={scope === 'entity'}
                onChange={() => setScope('entity')}
              />
              One entity
            </InlineLabel>
            <InlineLabel>
              <input
                type="radio"
                name="scope"
                value="eventSource"
                checked={scope === 'eventSource'}
                onChange={() => setScope('eventSource')}
              />
              One event source
            </InlineLabel>
            <InlineLabel>
              <input
                type="radio"
                name="scope"
                value="eventSourceType"
                checked={scope === 'eventSourceType'}
                onChange={() => setScope('eventSourceType')}
              />
              All sources of a type
            </InlineLabel>
          </div>
        </Field>

        {scope === 'entityType' && (
          <Field>
            <Label htmlFor="entityType">Entity type</Label>
            <Select
              id="entityType"
              value={entityType}
              onChange={(event) => setEntityType(event.target.value)}
            >
              {ENTITY_TYPES.map(type => (
                <option key={type} value={type}>{displayEntityType(type)}</option>
              ))}
            </Select>
          </Field>
        )}

        {scope === 'entity' && (
          <Field>
            <Label htmlFor="entityId">Entity ID</Label>
            <Input
              id="entityId"
              type="number"
              min={1}
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              placeholder="e.g. 42"
            />
          </Field>
        )}

        {scope === 'eventSource' && (
          <Field>
            <Label htmlFor="eventSourceId">Event Source ID</Label>
            <Input
              id="eventSourceId"
              type="number"
              min={1}
              value={eventSourceId}
              onChange={(e) => setEventSourceId(e.target.value)}
              placeholder="e.g. 123"
            />
          </Field>
        )}

        {scope === 'eventSourceType' && (
          <Field>
            <Label htmlFor="eventSourceTypeId">Event Source Type ID</Label>
            <Input
              id="eventSourceTypeId"
              type="number"
              min={1}
              value={eventSourceTypeId}
              onChange={(e) => setEventSourceTypeId(e.target.value)}
              placeholder="e.g. 1"
            />
          </Field>
        )}

        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 500 }}>Advanced</summary>
          <div style={{ marginTop: 16 }}>
            <Field>
              <Label>Mode</Label>
              <Select
                value={mode}
                onChange={(e) => setMode(e.target.value as Mode)}
              >
                <option value="allBatches">
                  Process all batches (recommended)
                </option>
                <option value="singleBatch">
                  Single batch (manual paging)
                </option>
              </Select>
            </Field>
            {mode === 'singleBatch' && (
              <Field>
                <Label htmlFor="offset">Starting offset</Label>
                <Input
                  id="offset"
                  type="number"
                  min={0}
                  value={offset}
                  onChange={(e) => setOffset(Number(e.target.value) || 0)}
                />
              </Field>
            )}
            <Field>
              <InlineLabel>
                <input
                  type="checkbox"
                  checked={forceExtraction}
                  onChange={(event) => setForceExtraction(event.target.checked)}
                />
                {/* This maps to the backend's explicit `forced` gate decision;
                  it does not merely refresh the status poll. */}
                Force AI extraction even when content is unchanged
              </InlineLabel>
              {forceExtraction && (
                <Banner tone="warning" style={{ marginTop: 8 }}>
                  This bypasses change and time-window savings and may materially increase AI and compute cost.
                </Banner>
              )}
            </Field>
          </div>
        </details>
      </Card>

      {result && (
        <Card>
          <CardTitle>Result</CardTitle>
          {result.success ? (
            <Banner tone={result.errorCount > 0 ? 'warning' : 'success'}>
              {result.message ?? 'Re-processing started successfully.'}
            </Banner>
          ) : (
            <Banner tone="error">
              {result.error ?? result.message ?? 'Re-processing failed.'}
            </Banner>
          )}

          <StatGrid>
            <Metric label="Enqueued" value={result.enqueuedCount.toLocaleString()} tone="success" />
            <Metric
              label="Errors"
              value={result.errorCount.toLocaleString()}
              tone={result.errorCount > 0 ? 'error' : 'neutral'}
            />
            {result.batchesProcessed != null && (
              <Metric label="Batches" value={result.batchesProcessed} />
            )}
            {result.totalRecords != null && (
              <Metric label="Total records" value={result.totalRecords.toLocaleString()} />
            )}
            <Metric
              label="Duration"
              value={`${(result.processingTime / 1000).toFixed(1)}s`}
            />
            {result.gateEnabled && result.runs && (
              <>
                <Metric label="Sources probed" value={result.runs.total.toLocaleString()} />
                <Metric label="AI candidates" value={(
                  result.runs.candidates + result.runs.dispatching + result.runs.dispatched +
                  result.runs.completed + result.runs.failed
                ).toLocaleString()} tone="warning" />
                <Metric label="Skipped unchanged" value={result.runs.skipped.toLocaleString()} tone="success" />
                <Metric label="Reader fail-open" value={result.runs.probeFailed.toLocaleString()} />
              </>
            )}
          </StatGrid>

          {result.totalRecordsInBatch === 0 && (
            <Banner tone="info">
              No records match this scope. Nothing to enqueue.
            </Banner>
          )}

          {result.hasMoreBatches && result.nextOffset != null && (
            <ButtonRow>
              <SecondaryButton onClick={handleAdvanceOffset}>
                <RefreshCw size={16} />
                Next batch (offset {result.nextOffset})
              </SecondaryButton>
            </ButtonRow>
          )}

          {Array.isArray(result.errors) && result.errors.length > 0 && (
            <>
              <CardTitle style={{ fontSize: 16, marginTop: 24 }}>Errors (sample)</CardTitle>
              <SampleTable>
                <thead>
                  <tr>
                    <th>EventSourceId</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.slice(0, 10).map((err, i) => {
                    const isObj = typeof err === 'object' && err !== null;
                    return (
                      <tr key={i}>
                        <td>{isObj ? (err as any).eventSourceId ?? '—' : '—'}</td>
                        <td>{isObj ? (err as any).message : String(err)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </SampleTable>
            </>
          )}

          <RawJson data={result} />
        </Card>
      )}

      <LastRunCard record={lastRun} />

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm re-processing"
        body={
          <>
            <p>
              You're about to start extraction preflight. In shadow mode every
              source still reaches the AI pipeline; in enforce mode only due or
              changed candidates use Azure OpenAI quota. Large scopes may take
              several hours to complete in the background.
            </p>
            <p style={{ marginTop: 12 }}>
              Scope: <strong>
                {scope === 'all'             && 'All records'}
                {scope === 'entityType'      && `${displayEntityType(entityType)} entities`}
                {scope === 'entity'          && `Entity ${entityId}`}
                {scope === 'eventSource'     && `Event Source ${eventSourceId}`}
                {scope === 'eventSourceType' && `Event Source Type ${eventSourceTypeId}`}
              </strong>
              <br />
              Batch size: <strong>{batchSize}</strong>
              {' · '}
              Mode: <strong>{mode === 'allBatches' ? 'all batches (client-side loop until done)' : `single batch @ offset ${offset}`}</strong>
            </p>
          </>
        }
        confirmPhrase="REPROCESS"
        primaryLabel="Start re-processing"
        isProcessing={false}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </AdminLayout>
  );
};

export default AdminReprocessPage;
